/**
 * Stock sheet print screen  (طباعة جرد المخزون / منتجات المندوب وكمياتها)
 *
 * A clean, printable A4 sheet listing the products available to the current
 * rep with their on-hand quantity (same source the Products screen shows:
 * `/sellgo_inventory`, which is already scoped to what the rep can sell) and
 * unit price.
 *
 * Route: /products/print/stock
 *
 * NEW screen — modifies no existing page. Uses the `.print-area` convention
 * so the global print CSS hides the app chrome.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Printer, ArrowRight, Share2, FileText, Receipt } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { shareElementAsImage } from '@/lib/shareDocument';
import { useCompanyInfo } from '@/lib/useCompanyInfo';

const fmtQty = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { maximumFractionDigits: 2 });
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const categName = (c) =>
  Array.isArray(c) ? (c[1] || '') : (typeof c === 'string' ? c : (c?.name || ''));

export default function StockPrint() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { odooSession } = useOdooAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [format, setFormat] = useState('a4'); // 'a4' | 'thermal'
  const printRef = useRef(null);
  const company = useCompanyInfo();

  // ?in_stock=1 → only products with qty > 0 (default shows everything).
  const inStockOnly = searchParams.get('in_stock') === '1';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 1000, page: 0, in_stock_only: inStockOnly || undefined };
      let res;
      try {
        res = await api.get('/sellgo_inventory', params);
      } catch {
        try { res = await api.get('/sellgo-products', params); }
        catch { res = await api.get('/products', params); }
      }
      if (res?.success) {
        let data = res.data || [];
        if (inStockOnly) data = data.filter((p) => Number(p.qty_available || 0) > 0);
        setRows(data);
      } else {
        setError(res?.error || 'فشل تحميل المنتجات');
      }
    } catch (e) {
      setError(e?.message || 'فشل تحميل المنتجات');
    } finally {
      setLoading(false);
    }
  }, [inStockOnly]);

  useEffect(() => { load(); }, [load]);

  // Switch paper size for printing (A4 vs thermal 80mm).
  useEffect(() => {
    document.documentElement.setAttribute('data-print-format', format);
    return () => document.documentElement.removeAttribute('data-print-format');
  }, [format]);

  const handleShare = async () => {
    if (!printRef.current) return;
    const t = toast.loading('جاري تجهيز الصورة...');
    try {
      const res = await shareElementAsImage(printRef.current, {
        filename: 'جرد-المخزون.jpg', title: 'جرد المخزون',
        captureWidth: format === 'thermal' ? undefined : 800,
      });
      toast.dismiss(t);
      if (res === 'downloaded') toast.success('تم حفظ الصورة');
    } catch {
      toast.dismiss(t);
      toast.error('تعذّر تجهيز الصورة');
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const totalQty = rows.reduce((s, r) => s + Number(r.qty_available || 0), 0);
  const dateLabel = new Date().toLocaleDateString('ar-EG', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });

  const isThermal = format === 'thermal';

  return (
    <div className="flex flex-col min-h-screen bg-muted/30" dir="rtl">
      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 flex items-center gap-3 bg-card border-b px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-muted">
          <ArrowRight className="w-4 h-4 text-muted-foreground" />
        </button>
        <h1 className="flex-1 text-lg font-bold text-foreground truncate">جرد المخزون</h1>
        <div className="flex bg-muted rounded-xl p-1">
          <button onClick={() => setFormat('a4')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${format === 'a4' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
            <FileText className="w-3.5 h-3.5" /> A4
          </button>
          <button onClick={() => setFormat('thermal')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${format === 'thermal' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground'}`}>
            <Receipt className="w-3.5 h-3.5" /> حراري 80مم
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto pb-28">
        <div ref={printRef} className={`print-area report-doc ${isThermal ? 'thermal' : ''} mx-auto my-6 bg-white rounded-lg shadow-sm text-[#1f2937] ${isThermal ? 'max-w-[340px] p-4' : 'max-w-[800px] p-8'}`} dir="rtl">
          <div className="flex items-start justify-between border-b pb-4 mb-5">
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">جرد المخزون</h1>
              <p className="text-sm text-gray-500 mt-1">{dateLabel}</p>
            </div>
            <div className="text-left flex items-center gap-2">
              {company.logoUrl && <img src={company.logoUrl} alt="" className="w-12 h-12 rounded-lg object-contain flex-shrink-0" />}
              <div>
                <div className="text-lg font-bold leading-tight">{company.name}</div>
                <div className="text-xs text-gray-400">{[company.address.city, company.address.country].filter(Boolean).join('، ')}</div>
              </div>
            </div>
          </div>

          <div className="flex justify-between text-sm mb-4 gap-2 flex-wrap">
            <span className="text-gray-500">المندوب: <span className="font-semibold text-gray-800">{odooSession?.name || '—'}</span></span>
            <span className="text-gray-500">عدد الأصناف: <span className="font-semibold text-gray-800">{rows.length}</span></span>
          </div>

          {rows.length === 0 ? (
            <p className="text-center text-gray-400 py-12">لا توجد منتجات.</p>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-gray-600">
                  <th className="text-right font-semibold py-2 px-2 border-b">#</th>
                  <th className="a4-only text-right font-semibold py-2 px-2 border-b">الكود</th>
                  <th className="text-right font-semibold py-2 px-2 border-b">المنتج</th>
                  <th className="text-center font-semibold py-2 px-2 border-b">الكمية</th>
                  <th className="a4-only text-left font-semibold py-2 px-2 border-b">السعر</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const qty = Number(r.qty_available || 0);
                  return (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="py-2 px-2 text-gray-400">{i + 1}</td>
                      <td className="a4-only py-2 px-2 text-gray-500 font-mono text-xs">{r.default_code || '—'}</td>
                      <td className="py-2 px-2 font-medium">
                        {r.name}
                        {categName(r.categ_id) && (
                          <span className="block text-xs text-gray-400">{categName(r.categ_id)}</span>
                        )}
                      </td>
                      <td className={`py-2 px-2 text-center font-bold ${qty > 0 ? 'text-emerald-700' : 'text-red-500'}`}>{fmtQty(qty)}</td>
                      <td className="a4-only py-2 px-2 text-left text-gray-700">{fmtMoney(r.list_price)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-800">
                  <td colSpan={isThermal ? 2 : 3} className="py-3 px-2 text-right font-bold">إجمالي الكميات</td>
                  <td className="py-3 px-2 text-center font-extrabold text-primary">{fmtQty(totalQty)}</td>
                  {!isThermal && <td className="py-3 px-2"></td>}
                </tr>
              </tfoot>
            </table>
          )}

          <div className="mt-10 flex justify-between text-xs text-gray-400 gap-2">
            <span>توقيع المندوب: ____________________</span>
            <span className="a4-only">توقيع المسؤول: ____________________</span>
          </div>
        </div>
      </div>

      {/* Action bar */}
      <div className="no-print fixed bottom-0 right-0 left-0 bg-card border-t border-border px-4 py-3 z-30 flex gap-2">
        <Button onClick={handleShare} variant="outline" className="flex-1 rounded-xl h-12 gap-2">
          <Share2 className="w-4 h-4" /> مشاركة
        </Button>
        <Button onClick={() => window.print()} className="flex-1 bg-primary rounded-xl h-12 gap-2">
          <Printer className="w-4 h-4" /> طباعة / حفظ PDF
        </Button>
      </div>
    </div>
  );
}
